import { useState, useCallback, useEffect } from 'react'
import { ApiService } from 'react-angle-dashboard-components'
import { useHistory, useLocation } from 'react-router-dom'
import { Paths } from '../routes/paths'
import { groupService, authService } from '../services'
import { Actions, GbsAction } from '../store/actions'
import { GbsState } from '../store/interfaces'

export const useInit = (
  dispatch: React.Dispatch<GbsAction>,
  zipCode: string | null
) => {
  const location = useLocation()
  const search = location.search
  const history = useHistory()
  const [isLoading, setIsLoading] = useState(true)

  const query = new URLSearchParams(search)
  const loginURLCode = query.get('code')
  const token = query.get('token') || undefined
  const zipURLCode = query.get('zip')
  const sessionStorageToken =
    sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken')

  const updateStoreWithBE = useCallback(() => {
    const bulkDispatch = (stateUpdate: { [key: string]: any }) => {
      const stepKeys: string[] = Object.keys(stateUpdate)

      stepKeys.forEach((stepKey) => {
        dispatch({
          type: Actions.SetStepData,
          payload: {
            stepKey: stepKey as keyof GbsState['steps'],
            stepData: stateUpdate[stepKey]
          }
        })
      })
    }

    setIsLoading(true)

    groupService
      .getInfo()
      .then((group) => {
        dispatch({
          type: Actions.SetInitialQuoteInformation,
          payload: {
            zip: parseInt(group.business_address?.zip || '0'),
            numberOfEmployees: group.total_individuals || 0
          }
        })

        const stateUpdate = {
          employeesAmount: { value: group.total_individuals || 0 },
          recommendedPlans: { avgAge: group.avg_age || 35 },
          currentlyMedical: { value: group.has_current_carrier },
          renewingCoverage: { value: group.current_carrier?.medical_term_date },
          planningPurchasing: { value: group.planned_purchase_timeframe },
          switchingCoverage: { value: group.current_changing_reason },
          newHealth: { value: group.new_insurance_reason },
          currentCarrier: {
            value: group?.current_carrier?.medical_carrier
          },
          finalQuote: {
            companyName: group?.group_name
          },
          contribution: {
            value: group.employer_contribution_employee_amount,
            dependentContribution: group.employer_contribution_dependent_amount,
            type: group.employer_contribution_amount_type
          },
          enrollment: {
            value: group.coverage_effective_date
          }
        }

        bulkDispatch(stateUpdate)
        setIsLoading(false)

        if (group.self_serve_status === 'complete') {
          const token = sessionStorage.getItem('accessToken')
          if (token) localStorage.setItem('accessToken', token)
          history.push(Paths.onboarding)
        } else if (group.group_name && location.pathname === '/') {
          const token = sessionStorage.getItem('accessToken')
          if (token) localStorage.setItem('accessToken', token)
          history.push(Paths.finalQuote)
        }
      })
      .catch((e) => {
        ApiService._token = null
        if (e.status === 401) {
          sessionStorage.removeItem('accessToken')
          history.push('/')
          history.go(0)
        }
      })
  }, [history, dispatch, location])

  const setTokenAndFillStore = useCallback(
    (token: string) => {
      if (!ApiService._token) {
        sessionStorage.setItem('accessToken', token)
        ApiService._token = token

        updateStoreWithBE()
      } else {
        setIsLoading(false)
      }
    },
    [updateStoreWithBE]
  )

  const getTokenFromURLCode = useCallback((loginURLCode: string) => {
    return authService.codeLogin(loginURLCode)
  }, [])

  const createGroup = useCallback((zip: string, token?: string) => {
    return groupService.create({
      zip: parseInt(zip),
      token: token
    })
  }, [])

  useEffect(() => {
    setIsLoading(true)

    if (loginURLCode) {
      // 1. If there is a code present on the URL, use it to fetch the access token.
      getTokenFromURLCode(loginURLCode).then((r: { access_token: string }) => {
        setTokenAndFillStore(r.access_token)
      })
    } else if (sessionStorageToken) {
      // 2. If there is a token on the session storage, use it to log in the user
      setTokenAndFillStore(sessionStorageToken)
    } else if (zipURLCode) {
      // 3. If there is a zip code on the URL, use it to create the group
      dispatch({
        type: Actions.SetZipCode,
        payload: zipURLCode
      })

      createGroup(zipURLCode, token)
        .then((response) => {
          if (response.access_token) {
            setTokenAndFillStore(response.access_token)
          }
        })
        .catch(() => {
          window.location.replace(
            'https://anglehealth.com/self-serve/out-of-range'
          )
        })
    } else if (location.pathname !== Paths.zipCode) {
      // 4. There is no token or zipcode, redirect the user to the zip code page

      history.replace(`${Paths.zipCode}${token ? `?token=${token}` : ''}`)
    }

    // if token and no zip code

    setIsLoading(false)
  }, [
    updateStoreWithBE,
    search,
    dispatch,
    history,
    loginURLCode,
    sessionStorageToken,
    zipURLCode,
    getTokenFromURLCode,
    setTokenAndFillStore,
    createGroup,
    location.pathname,
    token
  ])

  return { isLoading, updateStoreWithBE }
}
